import { isEqual } from "lodash";
import { Dispatch, useEffect, useEffectEvent, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  IValidReturn,
  IValidationFunction,
  IValidationTarget,
  formatJson,
  loadWrapper,
} from "../utility";
import { IRequestStatus, IValidationTargetOptions } from "../utility/interface";
import { ControlBase, ControlBaseProps } from "./ControlBase";
import { CopyText } from "./CopyText";
import { InTextLink } from "./InTextLink";

/**
 * Props for the Control210 component.
 */
export interface Control210Props extends ControlBaseProps {
  id: string;
  allowCarriageReturn?: boolean;
  allowCopy?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  forcePrefix?: string;
  invalidMessage?: string;
  isJsonText?: boolean;
  label: string;
  labelWidth?: number | "auto";
  maxWidthCols?: number;
  rows?: number;
  setValue?: Dispatch<string>;
  spellCheck?: boolean;
  subText?: string;
  type?: "string" | "number" | "textarea";
  validCondition?: IValidationFunction;
  validMessage?: string;
  validationTargetOptions?: IValidationTargetOptions;
  validationRequestUrl?: string;
  validationReturn?: (ret: boolean) => void;
  value: string;
  whiteSpaceFix?: boolean;
}

/**
 * A versatile text input control that supports both single-line text input and multi-line textarea functionality with customizable options.
 * Features include validation, JSON formatting, copy functionality, and responsive Bootstrap layout.
 *
 * @param props - Component props.
 * @param props.id - Unique identifier for the control
 * @param props.allowCarriageReturn - Whether to allow carriage returns in textarea (defaults to false)
 * @param props.allowCopy - Whether to show a copy button for the content (defaults to false)
 * @param props.autoFocus - Whether the input should automatically receive focus
 * @param props.disabled - Whether the control is disabled
 * @param props.forcePrefix - A prefix that must be present at the start of the value (defaults to "")
 * @param props.invalidMessage - Message to display when validation fails
 * @param props.isJsonText - Whether the content should be formatted as JSON (defaults to false)
 * @param props.label - Label text for the input field
 * @param props.labelWidth - Width of the label column in Bootstrap grid units or 'auto' (defaults to 2)
 * @param props.maxWidthCols - Maximum width in Bootstrap grid columns (defaults to 12)
 * @param props.rows - Number of rows for textarea type
 * @param props.setValue - Callback function to update the value
 * @param props.spellCheck - Whether spell checking is enabled (defaults to true)
 * @param props.subText - Additional help text displayed below the input
 * @param props.type - The type of input control to render (defaults to "string")
 * @param props.validCondition - Validation function to check the input value
 * @param props.validMessage - Message to display when validation passes
 * @param props.validationTargetOptions - Optional validation context options forwarded to the validation function
 * @param props.validationRequestUrl - URL for validation requests (if applicable)
 * @param props.validationReturn - Callback to receive validation results
 * @param props.value - Current value of the input
 * @param props.whiteSpaceFix - Whether to automatically trim whitespace on blur (defaults to false)
 * @param props.rest - Additional ControlBaseProps
 * @returns A React functional component rendering a Form.Group with label and input
 */
export const Control210 = ({
  id,
  allowCarriageReturn = false,
  allowCopy = false,
  autoFocus,
  disabled,
  forcePrefix = "",
  invalidMessage,
  isJsonText = false,
  label,
  labelWidth = 2,
  maxWidthCols = 12,
  rows,
  setValue,
  spellCheck = true,
  subText,
  type = "string",
  validCondition,
  validMessage,
  validationTargetOptions,
  validationRequestUrl = "",
  validationReturn,
  value,
  whiteSpaceFix = false,
  ...rest
}: Control210Props): React.ReactElement => {
  // Run validation check on current value
  const [validationStatus, setValidationStatus] = useState<IValidReturn>({
    success: false,
    errorText: "",
  });
  const [validationRequest, setValidationRequest] = useState<IRequestStatus<IValidationTarget>>({
    requesting: false,
    error: false,
  });
  const checkValidation = useEffectEvent(async (newValue: IValidationTarget) => {
    if (!validCondition) {
      setValidationStatus({
        success: true,
        data: {
          valid: true,
          validationText: "",
        },
      });
      setValidationRequest({ requesting: false, error: false, requestedId: newValue });
    } else {
      const response = await loadWrapper(
        validCondition,
        newValue,
        { ...validationRequest, error: false, errorText: undefined },
        setValidationRequest,
        validationRequestUrl,
      );
      /* istanbul ignore else */
      if (response) {
        setValidationStatus(response);
        validationReturn?.(response.success === false ? false : response.data.valid);
      }
    }
  });

  useEffect(() => {
    if (
      !validationRequest.requesting &&
      (value !== validationRequest.requestedId?.value ||
        !isEqual(validationTargetOptions, validationRequest.requestedId?.options))
    ) {
      checkValidation({ value, options: validationTargetOptions });
    }
  }, [validationRequest, validationTargetOptions, value]);

  return (
    <Form.Group
      controlId={`${id}`}
      as={Row}
      className="my-1"
    >
      <Form.Label
        column
        md={labelWidth}
      >
        {label}
      </Form.Label>
      <Col
        md={
          labelWidth === "auto"
            ? undefined
            : Math.max(allowCopy ? maxWidthCols - labelWidth - 1 : maxWidthCols - labelWidth, 1)
        }
      >
        <ControlBase
          {...rest}
          value={isJsonText ? formatJson(value) : value}
          setValue={(ret) => setValue?.(ret)}
          whiteSpaceFix={whiteSpaceFix}
          disabled={disabled || setValue === undefined}
          type={isJsonText ? "textarea" : type}
          forcePrefix={forcePrefix}
          rows={rows}
          allowCarriageReturn={allowCarriageReturn}
          className={`${
            validationStatus.success && !validationRequest.requesting
              ? validationStatus.data.valid === true
                ? "is-valid"
                : "is-invalid"
              : ""
          }`}
          spellCheck={spellCheck}
          autoFocus={autoFocus}
        />
        {(subText || validCondition) && (
          <Form.Text
            className={`${
              validationStatus.success && !validationRequest.requesting
                ? validationStatus.data?.valid === true
                  ? "valid-feedback"
                  : "invalid-feedback"
                : "text-muted"
            }`}
          >
            <InTextLink
              text={[
                subText ?? "",
                validationRequest.requesting
                  ? "updating status..."
                  : validationStatus.success
                    ? validationStatus.data.validationText !== ""
                      ? validationStatus.data.validationText
                      : validationStatus.data.valid === true
                        ? (validMessage ??
                          // istanbul ignore next
                          "")
                        : (invalidMessage ??
                          // istanbul ignore next
                          "")
                    : validationStatus.errorText,
              ]
                .filter((s) => s !== "")
                .join(", ")}
            />
          </Form.Text>
        )}
      </Col>
      {allowCopy && (
        <Col xs={1}>
          <CopyText value={value} />
        </Col>
      )}
    </Form.Group>
  );
};

Control210.displayName = "Control210";
